import Icon from "../NGMaterialIcon/NGMaterialIcon";
import { Button, CircularProgress, Menu } from "@mui/material";
import { ButtonProps } from "@mui/material/Button";
import MenuItem from "@mui/material/MenuItem";
import MenuList from "@mui/material/MenuList";
import { TooltipProps } from "@mui/material/Tooltip";
import { useSignal } from "@preact/signals-react";
import type { CsvCell, ExcelRow, GridApi } from "@ag-grid-community/core";
import type { AgGridReact } from "@ag-grid-community/react";
import React, { cloneElement, useRef } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { ListExportButton } from "../../../resolvers-types";
import { GridCsvExportProps, GridPrintExportProps, INGListExportButtonProps } from "../../library/NGFieldExtensions";
import { GetCustomLabelForListFromSiteSettings, GetSettingFromSite, setupLocalState } from "../../library/dataService";
import { getGridContext } from "../../library/metadataUtils";
import { getClassNameFromString, getsxObject, getTestId } from "../../library/utils";
import NGList from "./NGList";
import { getExportDataDefaults, IAnyObject } from "./NGListUtils";

const exceedsMaxSizePhrase =
  "There are more than 5000 rows in this export. If you would like to see more data, please contact support";

type ExportType = "csv" | "excel" | "print";

type LoadingStates = Record<ExportType, boolean>;

export interface GridExportDisplayOptions {
  /**
   * If `true`, this export option will be removed from the GridToolbarExport menu.
   * @default false
   */
  disableToolbarButton?: boolean;
}

export interface GridExportMenuItemProps<Options> {
  hideMenu?: () => void;
  options?: Options & GridExportDisplayOptions;
  apiRef: React.RefObject<AgGridReact<IAnyObject>>;
  exportFunction?: (callback: (maxSize: boolean, onlySelected: boolean) => void) => void;
  loading?: boolean;
}

export type GridCsvExportMenuItemProps = GridExportMenuItemProps<GridCsvExportProps>;

export type GridExcelExportMenuItemProps = GridExportMenuItemProps<GridPrintExportProps>;

export interface GridPrintExportMenuItemProps
  extends Omit<GridExportMenuItemProps<GridPrintExportProps>, "exportFunction"> {
  printFunction?: () => void;
}

export interface GridToolbarExportProps {
  csvOptions?: GridCsvExportProps & GridExportDisplayOptions;
  printOptions?: GridPrintExportProps & GridExportDisplayOptions;
  /**
   * The props used for each slot inside.
   * @default {}
   */
  slotProps?: { button?: Partial<ButtonProps>; tooltip?: Partial<TooltipProps> };
  apiRef: React.RefObject<AgGridReact<IAnyObject>>;
  [key: string]: unknown;
}

export function GridCsvExportMenuItem(props: GridCsvExportMenuItemProps) {
  const { hideMenu, options, apiRef, exportFunction, loading, ...other } = props;

  const getRows: () => CsvCell[][] = () => [
    [],
    [
      {
        data: {
          value: exceedsMaxSizePhrase,
          type: "String",
        },
      },
    ],
    [],
  ];

  const exportToCsv = async (maxSize = false, onlySelected = false) => {
    apiRef.current?.api.exportDataAsCsv({
      ...getExportDataDefaults(apiRef.current?.api),
      ...options,
      prependContent: maxSize ? getRows() : undefined,
      processCellCallback: (params) => {
        const colDef = params.column?.getColDef();
        if (colDef && colDef.cellDataType === "text") {
          return params.value ? `="${params.value}"` : "";
        }
        return params.value;
      },
      onlySelected: onlySelected,
    });
    hideMenu?.();
  };

  return (
    <MenuItem
      onClick={() => exportFunction?.(exportToCsv)}
      sx={{ minWidth: 175, alignItems: "center", justifyContent: "center" }}
      {...other}
    >
      {loading ? <CircularProgress size={20} /> : "Download as CSV"}
    </MenuItem>
  );
}

export function GridPrintExportMenuItem(props: GridPrintExportMenuItemProps) {
  const { hideMenu, apiRef, loading, printFunction, ...other } = props;

  function onBtPrint() {
    printFunction?.();
    setPrinterFriendly(apiRef.current?.api);

    setTimeout(() => {
      print();
      setNormal(apiRef.current?.api);
    }, 500);
  }

  function setPrinterFriendly(api?: GridApi) {
    api?.setGridOption("domLayout", "print");
  }

  function setNormal(api?: GridApi) {
    api?.setGridOption("domLayout", undefined);
  }

  return (
    <MenuItem
      onClick={() => {
        onBtPrint();
        hideMenu?.();
      }}
      sx={{ minWidth: 175, alignItems: "center", justifyContent: "center" }}
      {...other}
    >
      {loading ? <CircularProgress size={20} /> : "Print"}
    </MenuItem>
  );
}

export function GridExcelExportMenuItem(props: GridExcelExportMenuItemProps) {
  const { hideMenu, options, apiRef, exportFunction, loading, ...other } = props;

  const getRows: () => ExcelRow[] = () => [
    { cells: [] },
    {
      cells: [
        {
          data: {
            value: exceedsMaxSizePhrase,
            type: "String",
          },
        },
      ],
    },
    { cells: [] },
  ];

  const exportToExcel = async (maxSize = false, onlySelected = false) => {
    apiRef.current?.api.exportDataAsExcel({
      ...getExportDataDefaults(apiRef.current?.api),
      ...options,
      prependContent: maxSize ? getRows() : undefined,
      processCellCallback: (params) => {
        const colDef = params.column?.getColDef();
        if (colDef && colDef.cellDataType === "text") {
          return params.value ? `'${params.value}` : "";
        }
        return params.value;
      },
      onlySelected: onlySelected,
    });
    hideMenu?.();
  };

  return (
    <MenuItem
      onClick={() => exportFunction?.(exportToExcel)}
      {...other}
      sx={{ minWidth: 175, alignItems: "center", justifyContent: "center" }}
    >
      {loading ? <CircularProgress size={20} /> : "Download as Excel"}
    </MenuItem>
  );
}

interface GridToolbarExportContainerProps {
  slotProps?: { button?: Partial<ButtonProps>; tooltip?: Partial<TooltipProps> };
  children?: React.ReactNode;
  config: ListExportButton;
}

function GridToolbarExportContainer(props: GridToolbarExportContainerProps) {
  const { children, slotProps = {}, config } = props;
  const buttonProps = slotProps.button || {};
  const [open, setOpen] = React.useState(false);
  const buttonRef = React.useRef<HTMLButtonElement>(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setOpen((prevOpen) => !prevOpen);
    buttonProps.onClick?.(event);
  };

  const handleMenuClose = () => setOpen(false);

  if (children == null) {
    return null;
  }

  return (
    <React.Fragment>
      <Button
        sx={getsxObject(config.Style, {
          textTransform: "none",
        })}
        className={`${getClassNameFromString(config.Classes)}`}
        data-testid={props["data-testid"]}
        data-type={props["data-type"]}
        ref={buttonRef}
        startIcon={GetSettingFromSite("HideListExportButton") !== true && <Icon name="download" />}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-controls={open ? "export1" : undefined}
        id="export1"
        {...buttonProps}
        onClick={handleMenuOpen}
      >
        {GetCustomLabelForListFromSiteSettings("Export")}
      </Button>
      <Menu open={open} anchorEl={buttonRef.current} onClose={handleMenuClose}>
        <MenuList id="export1" autoFocusItem={open}>
          {React.Children.map(children, (child) => {
            if (!React.isValidElement(child)) {
              return child;
            }
            return React.cloneElement(child, { hideMenu: handleMenuClose } as { hideMenu: () => void });
          })}
        </MenuList>
      </Menu>
    </React.Fragment>
  );
}

function NGListExportButton({ config, context }: INGListExportButtonProps) {
  const local = setupLocalState(
    config,
    {
      Visible: useSignal(config.Visible ?? true),
    },
    context
  );

  const { Config, GridRef } = getGridContext(context);
  const clonedContainerId = "cloned-grid-container";
  const AgGridRef = useRef<AgGridReact<IAnyObject>>(null);
  const [loadingStates, setLoadingStates] = React.useState<LoadingStates>({
    csv: false,
    excel: false,
    print: false,
  });

  const exportTo = async (
    callback: (maxSize: boolean, onlySelected: boolean) => void,
    exportType: ExportType = "csv"
  ) => {
    // Set loading state based on export type
    setLoadingStates((prev) => ({ ...prev, [exportType]: true }));

    try {
      // Get the filter model from the original grid
      const filterModel = (GridRef as React.RefObject<AgGridReact<IAnyObject>>).current?.api.getFilterModel() || null;
      // Get selected rows
      const selectedNodes = (GridRef as React.RefObject<AgGridReact<IAnyObject>>).current?.api.getSelectedNodes();

      // Dynamically clone the AgGridReact component
      const clonedConfig = {
        ...Config,
        RowsPerPage: 5001,
        Visible: false,
        Id: `cloned-grid-${Config?.Id || "default"}-${Date.now()}`,
        Bindings: { ...Config?.Bindings, RowsPerPage: "5001" },
      };

      const clonedGrid = cloneElement(
        <BrowserRouter>
          <NGList config={clonedConfig} context={context} ref={AgGridRef} />
        </BrowserRouter>
      );

      // Append the cloned grid to the DOM and wait for it to be ready
      await appendGridToDOM(clonedGrid);

      // Apply filters and selections
      AgGridRef.current?.api.setFilterModel(filterModel);

      let onlySelected = false;
      if (selectedNodes && selectedNodes.length > 0) {
        AgGridRef.current?.api.setNodesSelected({ nodes: selectedNodes, newValue: true });
        onlySelected = true;
      }

      // Wait for data to be loaded
      await waitForDataReady();

      // Process the data
      const datasource = AgGridRef.current?.api.getGridOption("rowData");
      let maxSize = false;
      if (datasource && datasource.length > 5000) {
        AgGridRef.current?.api.setGridOption("rowData", datasource.slice(0, 5000));
        maxSize = true;
      }
      // Execute the callback
      callback(maxSize, onlySelected);

      // Cleanup
      const containerElement = document.getElementById(clonedContainerId);
      if (containerElement && containerElement.parentNode) {
        containerElement.parentNode.removeChild(containerElement);
      }
    } catch (error) {
      console.error("Export error:", error);
    } finally {
      // Always reset loading state
      setLoadingStates((prev) => ({ ...prev, [exportType]: false }));
    }
  };

  // Helper function to wait for data to be ready
  const waitForDataReady = (): Promise<void> => {
    return new Promise<void>((resolve) => {
      const checkDataReady = () => {
        if (AgGridRef.current?.api) {
          const isLoading = AgGridRef.current.api.getGridOption("loading");
          const datasource = AgGridRef.current.api.getGridOption("rowData");
          if (datasource && datasource.length > 0 && !isLoading) {
            resolve();
          } else {
            setTimeout(checkDataReady, 100);
          }
        } else {
          setTimeout(checkDataReady, 100);
        }
      };
      checkDataReady();
    });
  };

  // Function to dynamically append the cloned grid to the DOM and wait for it to be ready
  const appendGridToDOM = async (clonedGrid: React.ReactNode): Promise<void> => {
    const container = document.createElement("div");
    container.id = clonedContainerId;
    container.style.display = "none";

    document.body.appendChild(container);
    const root = createRoot(container);
    root.render(clonedGrid);

    // Wait for the grid to be mounted and ready
    return new Promise<void>((resolve) => {
      const checkGridReady = () => {
        if (AgGridRef.current?.api) {
          resolve();
        } else {
          setTimeout(checkGridReady, 100);
        }
      };
      checkGridReady();
    });
  };

  // Helper function to handle print loading state
  const handlePrintExport = () => {
    setLoadingStates((prev) => ({ ...prev, print: true }));
    setTimeout(() => setLoadingStates((prev) => ({ ...prev, print: false })), 600);
  };

  return local.Visible.value ? (
    <GridToolbarExportContainer data-testid={getTestId(config)} data-type={config.__typename} config={config}>
      {config.EnableCSV && (
        <GridCsvExportMenuItem
          apiRef={AgGridRef}
          exportFunction={(callback) => exportTo(callback, "csv")}
          loading={loadingStates.csv}
        />
      )}
      {config.EnableExcel && (
        <GridExcelExportMenuItem
          apiRef={AgGridRef}
          exportFunction={(callback) => exportTo(callback, "excel")}
          loading={loadingStates.excel}
        />
      )}
      {config.EnablePrint && (
        <GridPrintExportMenuItem apiRef={AgGridRef} loading={loadingStates.print} printFunction={handlePrintExport} />
      )}
    </GridToolbarExportContainer>
  ) : null;
}

export { GridToolbarExportContainer, NGListExportButton };
